<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'

import Icon from './Icon.vue'

const props = withDefaults(defineProps<{
  contentLabel?: string
  // we need a unique id for the aria-controls attribute
  // which has to be defined from the parent component
  // to ensure that the id is unique and working in ssr and csr
  controlsId: string
  maxLines?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8
  maxListItems?: number
  itemSelector?: string
  contentMode?: 'text' | 'list'
  // optional button labels for "show more" and "show less" states to override the default aria labels
  // example: { showMore: 'mehr anzeigen', showLess: 'weniger anzeigen' }
  buttonLabels?: { showMore: string, showLess: string }
}>(), {
  contentLabel: '',
  maxLines: 3,
  maxListItems: 3,
  itemSelector: 'li',
  contentMode: 'text'
})

const content = ref<HTMLElement | null>(null)
const savedScrollY = ref(0)
const showMore = ref(false)
const contentSmallerThanInitialSize = ref(false)

const ariaLabel = computed(() => showMore.value ? `weniger ${props.contentLabel} anzeigen` : `mehr ${props.contentLabel} anzeigen`)

const buttonLabel = computed(() => {
  if (props.buttonLabels?.showMore && props.buttonLabels?.showLess) {
    return showMore.value ? props.buttonLabels.showLess : props.buttonLabels.showMore
  }
  return ariaLabel.value
})

const ariaExpanded = computed(() => showMore.value ? 'true' : 'false')
const textMode = computed(() => props.contentMode === 'text')
const listMode = computed(() => props.contentMode === 'list')

// check if the content is smaller than the initial size
// and only display the show more button if necessary
const checkInitialTextOverflow = () => {
  const textContent = content.value!
  textContent.classList.remove('bx-line-clamp', `bx-line-clamp--${props.maxLines}`)
  const fullHeight = textContent.scrollHeight
  textContent.classList.add('bx-line-clamp', `bx-line-clamp--${props.maxLines}`)
  const clampedHeight = textContent.getBoundingClientRect().height
  contentSmallerThanInitialSize.value = fullHeight <= clampedHeight
}

// show the initial list items and display the show more button only if necessary
const showInitialListItems = () => {
  const listItems = content.value!.querySelectorAll(props.itemSelector)
  if (listItems.length > props.maxListItems) {
    for (let i = 0; i < props.maxListItems; i++) {
      listItems[i].classList.add('bx-js-visible')
    }
  } else {
    contentSmallerThanInitialSize.value = true
  }
}

const toggleShowMore = () => {
  if (!showMore.value) {
    savedScrollY.value = window.scrollY
    showMore.value = true
  } else {
    showMore.value = false
    window.scrollTo({
      top: savedScrollY.value,
      behavior: 'instant'
    })
  }
}

onMounted(() => {
  if (listMode.value) {
    showInitialListItems()
  } else if (textMode.value) {
    checkInitialTextOverflow()
  }
})
</script>

<template>
  <div class="bx-show-more bx-typo--show-more"
       :class="{
         'bx-js-show-more-hide': !showMore && !contentSmallerThanInitialSize,
         [`bx-show-more--${contentMode}`]: true
       }">
    <div :id="controlsId"
         ref="content"
         :class="{
           'bx-line-clamp': textMode && !showMore,
           [`bx-line-clamp--${maxLines}`]: textMode && !showMore
         }">
      <slot />
    </div>
    <button v-if="!contentSmallerThanInitialSize"
            tabindex="0"
            role="button"
            type="button"
            class="bx-show-more__toggle-button"
            :aria-controls="controlsId"
            :aria-label="ariaLabel"
            :aria-expanded="ariaExpanded"
            @click="toggleShowMore()">
      <span>{{ buttonLabel }}</span>
      <Icon name="arrow_toggle" />
    </button>
  </div>
</template>
